import { Injectable } from '@angular/core';
import { AnalyticsSiteAssets } from '@com-delta-idp/mnx-skymiles-landing-page-ui';
import {
  Banner,
  CardCarousel,
  MicroMoments,
  Offers,
  QuickLinks,
  SkyClubSpendTracker,
  Snapshot,
  Upgrades
} from '@com-delta-idp/mnx-skymiles-overview-ui';
import { DATA_ANALYTICS } from '../../models/analytics/data-layer.constants';

@Injectable({ providedIn: 'root' })
export class DataLayerAssetsService {
  setQuickLinkAssets(
    assets: AnalyticsSiteAssets[],
    quickLinks: QuickLinks
  ): void {
    assets.push(
      ...quickLinks.cards.map((card) => ({
        id: card.id,
        type: DATA_ANALYTICS.TILES,
        name: card.content.title,
        sub_type: DATA_ANALYTICS.AWARD,
        source: DATA_ANALYTICS.SOURCE,
        image_url: card.image.src
      }))
    );
  }

  setOfferAssets(assets: AnalyticsSiteAssets[], offers: Offers): void {
    assets.push(
      ...offers.cards.map((card) => ({
        id: card.id,
        type: DATA_ANALYTICS.TILES,
        name: card.content.title,
        sub_type: DATA_ANALYTICS.AWARD,
        source: DATA_ANALYTICS.SOURCE,
        image_url: card.image.src
      }))
    );
  }

  setCardAssets(
    assets: AnalyticsSiteAssets[],
    cardCarousel: CardCarousel
  ): void {
    assets.push(
      ...cardCarousel.cards.map((card) => ({
        id: card.labels.title.replace(/\s+/g, '').toLowerCase(),
        type: DATA_ANALYTICS.TILES,
        name: card.labels.title,
        sub_type: DATA_ANALYTICS.CARDS,
        source: DATA_ANALYTICS.SOURCE,
        image_url: card.image.src
      }))
    );
  }

  setUpGrades(assets: AnalyticsSiteAssets[], upgrades: Upgrades): void {
    assets.push(
      ...upgrades.cards.map((card) => ({
        id: card.title.replace(/\s+/g, '').toLowerCase(),
        type: DATA_ANALYTICS.BANNER,
        name: card.title,
        sub_type: DATA_ANALYTICS.AWARD,
        source: DATA_ANALYTICS.SOURCE,
        image_url: card.image.src
      }))
    );
  }

  setMicroMoments(
    assets: AnalyticsSiteAssets[],
    microMoments: MicroMoments
  ): void {
    assets.push({
      id: microMoments.id,
      type: DATA_ANALYTICS.BANNER,
      name: microMoments.title,
      sub_type: DATA_ANALYTICS.MICRO_MOMENTS,
      source: DATA_ANALYTICS.SOURCE,
      image_url: microMoments.logo.src
    });
  }

  setPartners(assets: AnalyticsSiteAssets[]): void {
    assets.push({
      id: `skymiles_${DATA_ANALYTICS.PARTNERS}`,
      type: DATA_ANALYTICS.BANNER,
      name: 'Earn With Our Partners',
      sub_type: DATA_ANALYTICS.PARTNERS,
      source: DATA_ANALYTICS.SOURCE
    });
  }

  setAmexBannerAssets(assets: AnalyticsSiteAssets[], banner: Banner): void {
    assets.push({
      id: DATA_ANALYTICS.AMEX_BANNER.replace(/\s+/g, '').toLowerCase(),
      type: DATA_ANALYTICS.BANNER,
      name: banner.offer.headline,
      sub_type: DATA_ANALYTICS.AWARD,
      source: DATA_ANALYTICS.SOURCE,
      image_url: banner.offer.image.src,
      membership_tier: banner.offer.cardType
    });
  }

  setSnapshotAssets(assets: AnalyticsSiteAssets[], snapshot: Snapshot): void {
    assets.push({
      id: DATA_ANALYTICS.SNAPSHOT.replace(/\s+/g, '').toLowerCase(),
      type: DATA_ANALYTICS.BANNER,
      name: snapshot.title,
      sub_type: DATA_ANALYTICS.AWARD,
      source: DATA_ANALYTICS.SOURCE
    });
  }

  setSkyClubAssets(
    assets: AnalyticsSiteAssets[],
    skyClub: SkyClubSpendTracker
  ): void {
    assets.push(
      ...skyClub.cards.map((card, index) => ({
        id: `${DATA_ANALYTICS.SKYCLUB_TRACKER}_${index}`,
        type: DATA_ANALYTICS.BANNER,
        name: card.type.name,
        sub_type: DATA_ANALYTICS.AWARD,
        source: DATA_ANALYTICS.SOURCE,
        image_url: card.visit.image.src
      }))
    );
  }

  updateAssetsPosition(assets: AnalyticsSiteAssets[]): AnalyticsSiteAssets[] {
    const bannerAssets = assets.filter((asset) => asset.type === 'banner');
    const tileAssets = assets.filter((asset) => asset.type === 'tile');
    bannerAssets.forEach((asset, index) => {
      asset.position = `${index + 1} of ${bannerAssets.length}`;
    });
    tileAssets.forEach((asset, index) => {
      asset.position = `${index + 1} of ${tileAssets.length}`;
    });
    return [...bannerAssets, ...tileAssets];
  }
}
